import React from "react";
import { Card, Typography } from "antd";
import { ClipboardList, BarChart2, SmileIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../../../context/LanguageContext";
import content from "../../../utils/content";

const { Title, Paragraph, Text } = Typography;

export function HowItWorksSection() {
  const { language } = useLanguage();
  const t = content[language].howItWorks;

  const animationSettings = {
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: "0px 0px -20% 0px",
  };

  const [refHeader, inViewHeader] = useInView(animationSettings);
  const [refCard1, inViewCard1] = useInView(animationSettings);
  const [refCard2, inViewCard2] = useInView(animationSettings);
  const [refCard3, inViewCard3] = useInView(animationSettings);

  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, delay },
    },
  });

  return (
    <div className="px-4 md:px-6 py-12 md:py-24" id="How-it-works">
      <div className="space-y-12">
        {/* Header */}
        <motion.div
          ref={refHeader}
          initial="hidden"
          animate={inViewHeader ? "visible" : "hidden"}
          variants={fadeInUp(0)}
          className="lg:grid lg:grid-cols-[70%_30%] flex flex-col items-center"
        >
          <Title
            level={2}
            className="lg:!text-[5rem] text-4xl text-center lg:text-left !font-bold tracking-tight p-[24px] bg-white"
          >
            {t.title}
            <span className="block text-[#FF7051]">
              {t.subtitle}
            </span>
          </Title>
          <Card
            className="bg-gray-50/50 shadow-md"
            bodyStyle={{ padding: "24px" }}
          >
            <Title level={3} className="!text-lg !mb-4">
              {t.benefits}
            </Title>
            <ul className="space-y-4">
              {t.benefitsList.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-[#FF7051] inline-block">→</span>
                  <div>
                    <Text strong>{benefit.title}:</Text>
                    <Text> {benefit.description}</Text>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </motion.div>

        {/* Steps */}
        <div className="relative grid gap-8 md:grid-cols-3 items-center">
          {t.steps.map((step, index) => {
            const ref = [refCard1, refCard2, refCard3][index];
            const inView = [inViewCard1, inViewCard2, inViewCard3][index];
            const Icon = [ClipboardList, BarChart2, SmileIcon][index];

            return (
              <motion.div
                key={index}
                ref={ref}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                variants={fadeInUp((index + 1) * 0.2)}
                className="flex flex-col h-full shadow-xl relative"
              >
                <Card bodyStyle={{ padding: "32px", height: "100%" }}>
                  <div className="mb-6 space-y-4">
                    <div className="p-3 bg-[#FF7051]/10 w-fit rounded-lg">
                      <Icon className="w-6 h-6 text-[#FF7051]" />
                    </div>
                    <Title level={4} className="!mb-2">
                      {step.title}
                    </Title>
                    <Paragraph className="text-gray-500 text-sm leading-relaxed">
                      {step.description}
                    </Paragraph>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default HowItWorksSection;
